import { useMemo, useState } from 'react';
import { Section, useViewport } from '@farmersdog/corgi-x';
import { AnimatePresence, motion } from 'motion/react';

import { ExtendedText } from 'src/pages/HomepageRedesign/components';
import { CategoryButton } from './CategoryButton';
import { DesktopReviewsCarousel } from './DesktopReviewsCarousel';

import { categories, ReviewCategory } from './categories';

import { getReorderedReviewsByCategory } from './getReorderedReviewsByCategory';

import { ReviewsRedesignSections } from '../../../HomepageRedesign/components/CtaTrackerV2';
import { useTrackedSectionRef } from '../../../../hooks/useTrackedSections';

import styles from './ReviewsCarousel.module.css';
import { MobileReviewsCarousel } from './MobileReviewsCarousel';

const INITIAL_SELECTED_CATEGORY = ReviewCategory.OverallHealth;

export const ReviewsCarousel = () => {
  const sectionRef = useTrackedSectionRef(ReviewsRedesignSections.Carousel);
  const [selectedCategory, setSelectedCategory] = useState<ReviewCategory>(
    INITIAL_SELECTED_CATEGORY
  );
  const { md: isDesktopViewport } = useViewport();

  const filteredItems = useMemo(() => {
    return getReorderedReviewsByCategory({
      category: selectedCategory,
    });
  }, [selectedCategory]);

  return (
    <Section aria-label="Customer Reviews section" sectionRef={sectionRef}>
      <ExtendedText
        as="h2"
        align="center"
        color="kale-3"
        variant="heading-28"
        weight="black"
        className={styles.heading}
      >
        See the fresh <br className={styles.mobile} />
        food difference
      </ExtendedText>

      <div className={styles.categories}>
        {Object.entries(categories).map(([key, label]) => (
          <CategoryButton
            key={key}
            isActive={key === selectedCategory}
            onClick={() => setSelectedCategory(key as ReviewCategory)}
          >
            {label}
          </CategoryButton>
        ))}
      </div>

      <AnimatePresence key={selectedCategory} mode="wait">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
          {!isDesktopViewport ? (
            <MobileReviewsCarousel items={filteredItems} />
          ) : (
            <DesktopReviewsCarousel items={filteredItems} />
          )}
        </motion.div>
      </AnimatePresence>
    </Section>
  );
};
